import { c as _c } from "react/compiler-runtime";
import { setIn, useField } from 'formik';
import { Fragment, useContext, useState } from 'react';
import { Grid, GridUnit } from '@hookdeck/theme';
import { DEFAULT_DESTINATION_DELIVERY_TIMEOUT_SECONDS, MAX_DESTINATION_DELIVERY_TIMEOUT_SECONDS } from '../../../../../../../../domain/destination';
import { APIDeliveryPolicy, APIDeliveryPolicyGroups, APIDestination, DeliveryGroupRateLimitPeriod, DeliveryGroupsConfig, DestinationAuthMethodConfig, DestinationAuthMethodType, DestinationHTTPMethod, DestinationRateLimitPeriod } from '../../../../../../../../typings/Destination.interface';
import { fieldName, isValidUrl } from '../../../../../utils';
import { rateLimitToSeconds } from '../../../../../utils/rateLimit';
import Button from '../../../../common/base/Button';
import { ClickableArea } from '../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../common/base/Card';
import Divider from '../../../../common/base/Divider';
import Icon from '../../../../common/base/Icon';
import Link from '../../../../common/base/Link';
import Text from '../../../../common/base/Text';
import Tooltip from '../../../../common/base/Tooltip';
import useEventPayloadFieldsSuggestions from '../../../../common/Form/Fields/hooks/useEventPayloadFieldsSuggestions';
import JSONFieldSelectorInput from '../../../../common/Form/Fields/JSONFieldSelectorInput';
import SelectInput from '../../../../common/Form/Fields/SelectInput';
import SwitchInput from '../../../../common/Form/Fields/SwitchInput';
import TextInput from '../../../../common/Form/Fields/TextInput';
import { Div } from '../../../../common/helpers/StyledUtils';
import resource_details_form_props from './resource_details';
import { SelectDestinationType } from '../../GuidedConnection/Steps/SelectDestinationType';
import Alert from '../../../../common/base/Alert';
import { DestinationType, isDestinationTypeFeatureUnmanaged } from '../../../../../../../../typings/Integration.interface';
import { DashboardContext } from '../../DashboardContext';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { DestinationFeature, DestinationFeatureType, MaxThroughputRateFeature, useFeatures } from './form_features';
import { cleanseFormErrorObject } from '../../../../../utils/form';
type ErrorOfFormValue<T> = { [P in keyof T]?: T[P] extends object ? ErrorOfFormValue<T[P]> : string | object };
export interface DestinationConfigurationFormValues {
  type?: DestinationType;
  enable_rate_limit: boolean;
  enable_delivery_groups: boolean;
  enable_custom_http_method: boolean;
  show_advanced: boolean;
  config: {
    rate_limit?: string;
    rate_limit_period?: DestinationRateLimitPeriod;
    delivery_timeout?: string;
    delivery_timeout_unit: DeliveryTimeoutUnit;
    http_method?: DestinationHTTPMethod | 'DEFAULT' | null;
    path_forwarding: boolean;
    url?: string | null;
    path?: string | null;
    auth?: DestinationAuthMethodConfig;
    auth_type?: DestinationAuthMethodType;
    delivery_groups: {
      key?: string;
      rate_limit?: string;
      rate_limit_period?: DeliveryGroupRateLimitPeriod;
      overrides: DeliveryGroupOverrideFormValue[];
    };
  };
}
export type DeliveryGroupOverrideFormValue = {
  key: string;
  rate_limit?: string;
  rate_limit_period?: DeliveryGroupRateLimitPeriod;
};
export type DeliveryGroupsFormState = {
  rate_limit?: string | number;
  rate_limit_period?: DestinationRateLimitPeriod;
  overrides?: DeliveryGroupOverrideFormValue[];
};
export type DeliveryTimeoutUnit = 'second' | 'minute';
export const MAX_DELIVERY_GROUP_LENGTH = 256;
export const DELIVERY_POLICY_RATE_PERIOD_OPTIONS = [{
  value: 'second',
  label: 'per second'
}, {
  value: 'minute',
  label: 'per minute'
}, {
  value: 'hour',
  label: 'per hour'
}];
const DELIVERY_TIMEOUT_UNIT_OPTIONS = [{
  value: 'second',
  label: 'seconds'
}, {
  value: 'minute',
  label: 'minutes'
}];
const getDeliveryTimeoutInSeconds = (timeout: string | number | undefined, unit: DeliveryTimeoutUnit) => Number(timeout) * (unit === 'minute' ? 60 : 1);
const DELIVERY_GROUP_OVERRIDE_RATE_PERIOD_OPTIONS = [{
  value: 'second',
  label: '/second'
}, {
  value: 'minute',
  label: '/minute'
}, {
  value: 'hour',
  label: '/hour'
}];
export const mapDeliveryGroupsConfigToPolicyGroups = (groups?: DeliveryGroupsConfig | null): APIDeliveryPolicyGroups | null => {
  if (!groups) {
    return null;
  }
  return {
    key: groups.key,
    rate: groups.rate_limit,
    rate_period: groups.rate_limit_period,
    overrides: groups.overrides ? Object.fromEntries(Object.entries(groups.overrides).map(([key, override]) => [key, {
      rate: override.rate_limit,
      rate_period: override.rate_limit_period
    }])) : null
  };
};
export const mapPolicyGroupsToDeliveryGroupsConfig = (groups?: APIDeliveryPolicyGroups | null): DeliveryGroupsConfig | null => {
  if (!groups) {
    return null;
  }
  return {
    key: groups.key,
    rate_limit: groups.rate,
    rate_limit_period: groups.rate_period,
    overrides: groups.overrides ? Object.fromEntries(Object.entries(groups.overrides).map(([key, override]) => [key, {
      rate_limit: override.rate,
      rate_limit_period: override.rate_period
    }])) : null
  };
};
export const getDestinationDeliveryPolicy = (destination?: APIDestination): APIDeliveryPolicy | null => {
  const config = destination?.config as (APIDestination['config'] & {
    delivery_policy?: APIDeliveryPolicy | null;
    rate_limit?: number | null;
    rate_limit_period?: DestinationRateLimitPeriod | null;
    delivery_groups?: DeliveryGroupsConfig | null;
  }) | undefined;
  if (!config) {
    return null;
  }
  if ('delivery_policy' in config) {
    return config.delivery_policy || null;
  }
  if ('rate_limit' in config || 'rate_limit_period' in config || 'delivery_groups' in config) {
    return {
      rate: config.rate_limit ?? null,
      period: config.rate_limit_period ?? null,
      groups: mapDeliveryGroupsConfigToPolicyGroups(config.delivery_groups)
    };
  }
  return null;
};
const getDeliveryTimeoutInitialConfig = (destination?: APIDestination) => {
  const timeout = destination?.config.delivery_timeout;
  if (timeout && timeout % 60 === 0) {
    return {
      delivery_timeout: String(timeout / 60),
      delivery_timeout_unit: 'minute' as const
    };
  }
  if (timeout) {
    return {
      delivery_timeout: String(timeout),
      delivery_timeout_unit: 'second' as const
    };
  }
  return {
    delivery_timeout: String(DEFAULT_DESTINATION_DELIVERY_TIMEOUT_SECONDS / 60),
    delivery_timeout_unit: 'minute' as const
  };
};
export const getProjectThroughputDefaultRate = (max_events_per_second?: number | null) => max_events_per_second ? String(max_events_per_second) : '5';
export const getDeliveryRateDefault = ({
  max_events_per_second,
  enable_rate_limit,
  rate_limit,
  rate_limit_period
}: {
  max_events_per_second?: number | null;
  enable_rate_limit?: boolean;
  rate_limit?: string | number | null;
  rate_limit_period?: DestinationRateLimitPeriod | null;
}): {
  rate_limit: string;
  rate_limit_period: DeliveryGroupRateLimitPeriod;
} => {
  if (enable_rate_limit && Number(rate_limit) > 0 && rate_limit_period && rate_limit_period !== 'concurrent') {
    return {
      rate_limit: String(rate_limit),
      rate_limit_period: rate_limit_period
    };
  }
  return {
    rate_limit: getProjectThroughputDefaultRate(max_events_per_second),
    rate_limit_period: 'second'
  };
};
export const getDeliveryRateDefaultFromDestination = (destination: APIDestination, max_events_per_second?: number | null) => {
  const delivery_policy = getDestinationDeliveryPolicy(destination);
  return getDeliveryRateDefault({
    max_events_per_second,
    enable_rate_limit: !!delivery_policy?.rate,
    rate_limit: delivery_policy?.rate || null,
    rate_limit_period: delivery_policy?.period || null
  });
};
export const getDeliveryRateLimitWarning = ({
  project_throughput,
  normalized_rate_limit,
  enable_rate_limit,
  enable_delivery_groups,
  delivery_groups
}: {
  project_throughput: number;
  normalized_rate_limit?: number;
  enable_rate_limit: boolean;
  enable_delivery_groups: boolean;
  delivery_groups?: DeliveryGroupsFormState;
}): string | null => {
  const destination_max_rate = enable_rate_limit && normalized_rate_limit ? normalized_rate_limit : undefined;
  if (normalized_rate_limit && normalized_rate_limit > project_throughput) {
    return 'Your max delivery rate value is higher than your project throughput limit, which determines the effective delivery rate. The delivery rate will be limited to your project throughput.';
  }
  if (!enable_delivery_groups || !delivery_groups) {
    return null;
  }
  const group_rate = Number(delivery_groups.rate_limit) && delivery_groups.rate_limit_period ? rateLimitToSeconds(Number(delivery_groups.rate_limit), delivery_groups.rate_limit_period) : undefined;
  if (group_rate) {
    if (destination_max_rate && group_rate > destination_max_rate) {
      return "Your group's max delivery rate is above the destination max delivery rate. The group's rate will be limited by the destination max delivery rate.";
    }
    if (group_rate > project_throughput) {
      return "Your group's max delivery rate is above your project throughput. The group's rate will be limited by your project throughput.";
    }
  }
  const overrides = delivery_groups.overrides || [];
  if (overrides.some(override => override.rate_limit && destination_max_rate && rateLimitToSeconds(Number(override.rate_limit), override.rate_limit_period || 'second') > destination_max_rate)) {
    return "A delivery rate override is above the destination max delivery rate. While that's allowed input, affected groups will be limited to the destination max delivery rate.";
  }
  if (overrides.some(override => override.rate_limit && rateLimitToSeconds(Number(override.rate_limit), override.rate_limit_period || 'second') > project_throughput)) {
    return "A delivery rate override is above your project throughput. While that's allowed input, affected groups will be limited by your project throughput.";
  }
  return null;
};
const getDeliveryGroupsInitialConfig = (destination?: APIDestination, max_events_per_second?: number | null) => {
  const default_delivery_rate = destination ? getDeliveryRateDefaultFromDestination(destination, max_events_per_second) : getDeliveryRateDefault({
    max_events_per_second
  });
  const delivery_groups = mapPolicyGroupsToDeliveryGroupsConfig(getDestinationDeliveryPolicy(destination)?.groups);
  return {
    enable_delivery_groups: !!delivery_groups,
    delivery_groups: {
      key: delivery_groups?.key || '',
      rate_limit: delivery_groups?.rate_limit ? String(delivery_groups.rate_limit) : default_delivery_rate.rate_limit,
      rate_limit_period: delivery_groups?.rate_limit_period || default_delivery_rate.rate_limit_period,
      overrides: Object.entries(delivery_groups?.overrides || {}).map(([key, override]) => ({
        key,
        rate_limit: String(override.rate_limit),
        rate_limit_period: override.rate_limit_period
      }))
    }
  };
};
export const validateDeliveryGroupsConfiguration = async (values: DestinationConfigurationFormValues, supports_delivery_groups: boolean) => {
  let errors = {} as Record<string, object | string | undefined>;
  if (!supports_delivery_groups) {
    return errors;
  }
  if (values.enable_delivery_groups && Number(values.config.delivery_groups.rate_limit) <= 0) {
    errors = setIn(errors, 'config.delivery_groups.rate_limit', 'Must be greater than 0');
  }
  if (values.enable_delivery_groups && !values.config.delivery_groups.key?.trim()) {
    errors = setIn(errors, 'config.delivery_groups.key', 'Required');
  }
  if (values.enable_delivery_groups && values.config.delivery_groups.key?.includes(',')) {
    errors = setIn(errors, 'config.delivery_groups.key', 'Only one field is supported');
  }
  values.config.delivery_groups.overrides.forEach((override, index) => {
    if (override.key && override.key.length > MAX_DELIVERY_GROUP_LENGTH) {
      errors = setIn(errors, `config.delivery_groups.overrides.${index}.key`, `Must be ${MAX_DELIVERY_GROUP_LENGTH} characters or less`);
    }
    if (override.key && Number(override.rate_limit) <= 0) {
      errors = setIn(errors, `config.delivery_groups.overrides.${index}.rate_limit`, 'Must be greater than 0');
    }
  });
  return errors;
};
export const DeliveryGroupOverridesTable = t0 => {
  const $ = _c(29);
  const {
    default_rate,
    default_rate_period,
    prefix: t1,
    show_heading: t2
  } = t0;
  const prefix = t1 === undefined ? "" : t1;
  const show_heading = t2 === undefined ? true : t2;
  let t3;
  if ($[0] !== prefix) {
    t3 = fieldName("config.delivery_groups.overrides", prefix);
    $[0] = prefix;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [t4,, t5] = useField(t3);
  const {
    value: overrides
  } = t4;
  const {
    setValue: setOverrides
  } = t5;
  let t6;
  if ($[2] !== default_rate || $[3] !== default_rate_period || $[4] !== overrides || $[5] !== setOverrides) {
    t6 = () => {
      setOverrides([...(overrides || []), {
        key: "",
        rate_limit: default_rate,
        rate_limit_period: default_rate_period
      }]);
    };
    $[2] = default_rate;
    $[3] = default_rate_period;
    $[4] = overrides;
    $[5] = setOverrides;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const addOverride = t6;
  let t7;
  if ($[7] !== overrides || $[8] !== setOverrides) {
    t7 = index => {
      setOverrides((overrides || []).filter((_, i) => i !== index));
    };
    $[7] = overrides;
    $[8] = setOverrides;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const removeOverride = t7;
  let t8;
  if ($[10] !== show_heading) {
    t8 = show_heading && <Div flex={{
      align: "center",
      grow: true,
      gap: 2
    }} m={{
      b: 4
    }}><Text semi={true} text_wrap={false} muted={true} size="xs">Field Value Overrides</Text><Divider /></Div>;
    $[10] = show_heading;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      justify: "space-between"
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Div w={50} flex={t9}><Text size="xs" semi={true} muted={true} p={{
        x: 4,
        y: 1
      }}>Field Value</Text></Div>;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <StyledCardSection flex={true} muted={true}>{t10}<Div w={50}><Text size="xs" semi={true} muted={true} p={{
          x: 2,
          y: 1
        }}>Max Delivery Rate</Text></Div></StyledCardSection>;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== default_rate || $[16] !== overrides || $[17] !== prefix || $[18] !== removeOverride) {
    t12 = overrides?.length > 0 && <StyledCardSection scroll={true} max_h={{
      px: 200
    }}>{overrides.map((override, index_0) => <Fragment key={index_0}><Div grid={{
          columns: 2
        }} style={{
          alignItems: "start"
        }}><Div p={{
            l: 4,
            r: 2,
            y: 3
          }} flex={{
            justify: "space-between"
          }}><TextInput w={100} name={fieldName(`config.delivery_groups.overrides.${index_0}.key`, prefix)} placeholder="customer_123" mono={true} m={0} /></Div><Div p={{
            l: 2,
            r: 2,
            y: 3
          }} grid={{
            columns: "minmax(0, 1fr) 112px auto",
            gap: 2
          }} style={{
            alignItems: "start"
          }}><Div min_w={{
              raw: "0"
            }}><TextInput name={fieldName(`config.delivery_groups.overrides.${index_0}.rate_limit`, prefix)} type="number" min={1} required={true} default_value={default_rate} m={0} /></Div><Div min_w={{
              raw: "0"
            }}><SelectInput name={fieldName(`config.delivery_groups.overrides.${index_0}.rate_limit_period`, prefix)} options={DELIVERY_GROUP_OVERRIDE_RATE_PERIOD_OPTIONS} required={true} block={true} m={0} /></Div><Button ghost={true} icon="delete" large={true} onClick={() => removeOverride(index_0)} aria-label={`Remove ${override.key || "override"}`} /></Div></Div>{index_0 < overrides.length - 1 && <Divider />}</Fragment>)}</StyledCardSection>;
    $[15] = default_rate;
    $[16] = overrides;
    $[17] = prefix;
    $[18] = removeOverride;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      x: 2,
      y: 2
    };
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== addOverride) {
    t14 = <StyledCardSection p={t13}><Button ghost={true} block={true} icon="add" onClick={addOverride}>Add field value override</Button></StyledCardSection>;
    $[21] = addOverride;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t12 || $[24] !== t14) {
    t15 = <StyledCard>{t11}{t12}{t14}</StyledCard>;
    $[23] = t12;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t15 || $[27] !== t8) {
    t16 = <>{t8}{t15}</>;
    $[26] = t15;
    $[27] = t8;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  return t16;
};
export const DeliveryGroupsConfigurationFields = ({
  prefix,
  destination_id
}: {
  prefix: string;
  destination_id?: string;
}) => {
  const [{
    value: enable_delivery_groups
  }] = useField<boolean>(fieldName('enable_delivery_groups', prefix));
  const [{
    value: enable_rate_limit
  }] = useField<boolean>(fieldName('enable_rate_limit', prefix));
  const [{
    value: rate_limit
  }] = useField<number>(fieldName('config.rate_limit', prefix));
  const [{
    value: rate_limit_period
  }] = useField<DestinationRateLimitPeriod>(fieldName('config.rate_limit_period', prefix));
  const {
    team
  } = useContext(DashboardContext);
  const delivery_rate_default = getDeliveryRateDefault({
    max_events_per_second: team?.max_events_per_second,
    enable_rate_limit,
    rate_limit,
    rate_limit_period
  });
  const useSuggestionsWithParams = () => useEventPayloadFieldsSuggestions({
    destination_id
  });
  return <StyledCard overflow_hidden dashed={!enable_delivery_groups}>
      <StyledCardSection p={{
      x: 4,
      y: 3
    }}>
        <Div flex={{
        justify: 'flex-start',
        align: 'center',
        gap: 3
      }}>
          <SwitchInput name={fieldName('enable_delivery_groups', prefix)} />
          <Text semi muted={!enable_delivery_groups} flex={{
          align: 'center'
        }}>
            Delivery rate by group
            <Tooltip tooltip="Group deliveries by a payload field and apply delivery limits per group.">
              <Icon muted right={1.5} small pointer icon="info" />
            </Tooltip>
          </Text>
        </Div>
      </StyledCardSection>
      <StyledCardSection borderless={!enable_delivery_groups}>
        {enable_delivery_groups && <StyledCard overflow_hidden borderless>
            <StyledCardSection p={4}>
              <Div m={{
            b: 4
          }}>
                <JSONFieldSelectorInput name={fieldName('config.delivery_groups.key', prefix)} label="Field Key" required placeholder="e.g. 'body.customer_id'" bordered max_tokens={1} useSuggestions={useSuggestionsWithParams} />
              </Div>
              <Text as="label" htmlFor={fieldName('config.delivery_groups.rate_limit', prefix)} muted semi size="xs">
                Group Max Delivery Rate
                <Text as="span" danger>
                  *
                </Text>
              </Text>
              <Grid>
                <GridUnit size={1 / 2}>
                  <TextInput m={{
                r: 2,
                b: 0
              }} name={fieldName('config.delivery_groups.rate_limit', prefix)} default_value={delivery_rate_default.rate_limit} type="number" min={1} required />
                </GridUnit>
                <GridUnit size={1 / 2}>
                  <SelectInput m={{
                l: 2,
                b: 0
              }} block name={fieldName('config.delivery_groups.rate_limit_period', prefix)} options={DELIVERY_POLICY_RATE_PERIOD_OPTIONS} required />
                </GridUnit>
              </Grid>
              <Div m={{
            t: 4
          }}>
                <DeliveryGroupOverridesTable prefix={prefix} default_rate={delivery_rate_default.rate_limit} default_rate_period={delivery_rate_default.rate_limit_period} />
              </Div>
            </StyledCardSection>
          </StyledCard>}
      </StyledCardSection>
    </StyledCard>;
};
const supportsDeliveryGroups = (feature: DestinationFeatureType) => {
  return isDestinationTypeFeatureUnmanaged(feature) && feature.delivery_groups === true;
};
const DeliveryTimeoutFeature = t0 => {
  const $ = _c(18);
  const {
    prefix
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      y: 4
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      x: 4,
      y: 3
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      align: "center"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <StyledCardSection p={t2}><Text semi={true} flex={t3}>Delivery timeout<Tooltip tooltip="Set how long Hookdeck waits for this destination. The default is 1 minute and the maximum is 15 minutes."><Icon muted={true} right={1.5} small={true} pointer={true} icon="info" /></Tooltip></Text></StyledCardSection>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      x: 4,
      y: 4,
      t: 0
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      r: 2,
      b: 0
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== prefix) {
    t7 = fieldName("config.delivery_timeout", prefix);
    $[6] = prefix;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t7) {
    t8 = <GridUnit size={0.5}><TextInput m={t6} name={t7} default_value="1" type="number" min={1} required={true} /></GridUnit>;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      l: 2,
      b: 0
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== prefix) {
    t10 = fieldName("config.delivery_timeout_unit", prefix);
    $[11] = prefix;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10) {
    t11 = <GridUnit size={0.5}><SelectInput m={t9} block={true} name={t10} options={DELIVERY_TIMEOUT_UNIT_OPTIONS} required={true} /></GridUnit>;
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11 || $[16] !== t8) {
    t12 = <Div m={t1}><StyledCard overflow_hidden={true}>{t4}<StyledCardSection p={t5} borderless={true}><Grid>{t8}{t11}</Grid></StyledCardSection></StyledCard></Div>;
    $[15] = t11;
    $[16] = t8;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  return t12;
};
export const DeliveryPolicyConfiguration = t0 => {
  const $ = _c(31);
  const {
    feature,
    prefix,
    destination_id,
    show_delivery_groups,
    show_title: t1
  } = t0;
  const show_title = t1 === undefined ? false : t1;
  const [show_policy, setShowPolicy] = useState(true);
  let t2;
  if ($[0] !== prefix) {
    t2 = fieldName("enable_delivery_groups", prefix);
    $[0] = prefix;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [t3] = useField(t2);
  const {
    value: enable_delivery_groups
  } = t3;
  let t4;
  if ($[2] !== prefix) {
    t4 = fieldName("enable_rate_limit", prefix);
    $[2] = prefix;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const [t5] = useField(t4);
  const {
    value: enable_rate_limit
  } = t5;
  let t6;
  if ($[4] !== prefix) {
    t6 = fieldName("config.rate_limit", prefix);
    $[4] = prefix;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const [t7] = useField(t6);
  const {
    value: rate_limit
  } = t7;
  let t8;
  if ($[6] !== prefix) {
    t8 = fieldName("config.rate_limit_period", prefix);
    $[6] = prefix;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const [t9] = useField(t8);
  const {
    value: rate_limit_period
  } = t9;
  let t10;
  if ($[8] !== prefix) {
    t10 = fieldName("config.delivery_groups", prefix);
    $[8] = prefix;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const [t11] = useField(t10);
  const {
    value: delivery_groups
  } = t11;
  const {
    team
  } = useContext(DashboardContext);
  let t12;
  if ($[10] !== delivery_groups || $[11] !== enable_delivery_groups || $[12] !== enable_rate_limit || $[13] !== rate_limit || $[14] !== rate_limit_period || $[15] !== team?.max_events_per_second) {
    const normalized_rate_limit = Number(rate_limit) && rate_limit_period && rate_limit_period !== "concurrent" ? rateLimitToSeconds(rate_limit, rate_limit_period) : undefined;
    t12 = getDeliveryRateLimitWarning({
      project_throughput: team?.max_events_per_second || 0,
      normalized_rate_limit,
      enable_rate_limit,
      enable_delivery_groups,
      delivery_groups
    });
    $[10] = delivery_groups;
    $[11] = enable_delivery_groups;
    $[12] = enable_rate_limit;
    $[13] = rate_limit;
    $[14] = rate_limit_period;
    $[15] = team?.max_events_per_second;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const warning = t12;
  let t13;
  if ($[17] !== show_policy || $[18] !== show_title) {
    t13 = show_title && <Div flex={{
      align: "center",
      gap: 2
    }} m={{
      b: 4
    }}><ClickableArea p={{
        y: 0,
        x: 1
      }} m={{
        l: -1
      }} rounded={true} style={{
        width: "auto"
      }} flex={{
        justify: "flex-start",
        align: "center",
        gap: 1
      }} onClick={() => setShowPolicy(!show_policy)}><Text size="xs" semi={true} muted={!show_policy}>Delivery policy</Text><Icon muted={true} icon={show_policy ? "arrow_drop_down" : "arrow_right"} /></ClickableArea>{show_policy && <Divider />}</Div>;
    $[17] = show_policy;
    $[18] = show_title;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== destination_id || $[21] !== feature || $[22] !== prefix || $[23] !== show_delivery_groups || $[24] !== show_policy || $[25] !== team?.max_events_per_second || $[26] !== warning) {
    t14 = show_policy && <StyledCard overflow_hidden={true} warning={!!warning}><StyledCardSection muted={true} p={{
        x: 4,
        y: 3
      }} flex={{
        justify: "space-between",
        align: "center"
      }}><Text semi={true} flex={{
          align: "center",
          gap: 2
        }}>Project throughput<Tooltip tooltip="The maximum number of events per second that can be delivered to this destination."><Icon icon="info" muted={true} /></Tooltip></Text><Div flex={{
          align: "center",
          gap: 2
        }}><Text muted={true}>{team?.max_events_per_second} events per second</Text><Text muted={true}>·</Text><Link primary={true} icon="link" to="/settings/project/quotas?view=throughput">Modify</Link></Div></StyledCardSection><StyledCardSection p={4} flex={{
        direction: "column",
        gap: 4
      }}><MaxThroughputRateFeature feature={feature} prefix={prefix} />{show_delivery_groups && <DeliveryGroupsConfigurationFields prefix={prefix} destination_id={destination_id} />}</StyledCardSection>{warning && <StyledCardSection p={{
        y: 3,
        x: 4
      }} warning={true} flex={{
        align: "flex-start",
        gap: 3
      }}><Icon icon="warning" warning={true} m={0.5} /><Text>{warning}</Text></StyledCardSection>}</StyledCard>;
    $[20] = destination_id;
    $[21] = feature;
    $[22] = prefix;
    $[23] = show_delivery_groups;
    $[24] = show_policy;
    $[25] = team?.max_events_per_second;
    $[26] = warning;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t13 || $[29] !== t14) {
    t15 = <>{t13}{t14}</>;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
};
const DestinationConfigurationFeature = t0 => {
  const $ = _c(3);
  const {
    feature,
    prefix
  } = t0;
  let t1;
  if ($[0] !== feature || $[1] !== prefix) {
    t1 = <DestinationFeature feature={feature} prefix={prefix} />;
    $[0] = feature;
    $[1] = prefix;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const destination_configuration_form_props = {
  postprocessValues: (values: DestinationConfigurationFormValues, supports_delivery_groups = true) => {
    const overrides = Object.fromEntries((values.config.delivery_groups?.overrides || []).filter(override => override.key && override.rate_limit && override.rate_limit_period).map(override => [override.key, {
      rate: Number(override.rate_limit),
      rate_period: override.rate_limit_period
    }]));
    return {
      type: values.type,
      config: {
        path: values.type === 'CLI' ? values.config.path : null,
        http_method: values?.config?.http_method !== 'DEFAULT' ? values.config.http_method : null,
        url: values.config.url || null,
        delivery_policy: {
          rate: values.enable_rate_limit ? Number(values.config.rate_limit) : null,
          period: values.enable_rate_limit ? values.config.rate_limit_period : null,
          groups: supports_delivery_groups && values.enable_delivery_groups ? {
            key: values.config.delivery_groups.key?.trim(),
            rate: Number(values.config.delivery_groups.rate_limit),
            rate_period: values.config.delivery_groups.rate_limit_period,
            overrides: Object.keys(overrides).length > 0 ? overrides : null
          } : null
        },
        delivery_timeout: getDeliveryTimeoutInSeconds(values.config.delivery_timeout, values.config.delivery_timeout_unit),
        path_forwarding_disabled: !values.config.path_forwarding,
        auth_type: values.config.auth_type || null,
        auth: values.config.auth || null
      }
    };
  },
  getInitialValues: (destination?: APIDestination, default_type?: DestinationType, max_events_per_second?: number | null): DestinationConfigurationFormValues => {
    const delivery_groups_initial_values = getDeliveryGroupsInitialConfig(destination, max_events_per_second);
    const delivery_timeout_initial_values = getDeliveryTimeoutInitialConfig(destination);
    if (destination) {
      const delivery_policy = getDestinationDeliveryPolicy(destination);
      return {
        type: destination.type as DestinationType | undefined,
        enable_rate_limit: !!delivery_policy?.rate,
        enable_delivery_groups: delivery_groups_initial_values.enable_delivery_groups,
        enable_custom_http_method: 'http_method' in destination.config && destination.config.http_method ? true : false,
        show_advanced: !!destination.config.path_forwarding_disabled || !!destination.config.http_method || destination.config.auth_type !== 'HOOKDECK_SIGNATURE',
        config: {
          ...(destination.config || {}),
          http_method: destination.config.http_method || 'DEFAULT',
          rate_limit: delivery_policy?.rate ? String(delivery_policy.rate) : undefined,
          rate_limit_period: delivery_policy?.period || 'second',
          delivery_timeout: delivery_timeout_initial_values.delivery_timeout,
          delivery_timeout_unit: delivery_timeout_initial_values.delivery_timeout_unit,
          delivery_groups: delivery_groups_initial_values.delivery_groups,
          path_forwarding: 'path_forwarding_disabled' in destination.config ? !destination.config.path_forwarding_disabled : true,
          auth_type: destination.config.auth_type || 'HOOKDECK_SIGNATURE',
          auth: destination.config.auth || {}
        } as DestinationConfigurationFormValues['config']
      };
    }
    return {
      type: default_type,
      enable_rate_limit: false,
      enable_delivery_groups: false,
      enable_custom_http_method: false,
      show_advanced: false,
      config: {
        http_method: 'DEFAULT',
        path_forwarding: true,
        rate_limit: undefined,
        rate_limit_period: 'second',
        delivery_timeout: delivery_timeout_initial_values.delivery_timeout,
        delivery_timeout_unit: delivery_timeout_initial_values.delivery_timeout_unit,
        delivery_groups: delivery_groups_initial_values.delivery_groups,
        auth_type: 'HOOKDECK_SIGNATURE',
        auth: {}
      }
    };
  },
  validate: async (values: DestinationConfigurationFormValues, supports_delivery_groups = true) => {
    let errors: ErrorOfFormValue<DestinationConfigurationFormValues> = {};
    if (!values.type) {
      errors.type = 'Required';
    }
    if (values.type === 'HTTP') {
      if (!values.config?.url || values.config?.url.length === 0) {
        errors.config = errors.config ? {
          ...(errors.config as object),
          url: 'Required'
        } : {
          url: 'Required'
        };
      }
      if (values.config?.url) {
        if (!isValidUrl(values.config.url)) {
          errors.config = errors.config ? {
            ...(errors.config as object),
            url: 'Must be a valid HTTP url'
          } : {
            url: 'Must be a valid HTTP url'
          };
        }
      }
    } else if (values.type === 'CLI') {
      if (!values.config?.path || values.config?.path.length === 0) {
        errors.config = errors.config ? {
          ...(errors.config as object),
          path: 'Required'
        } : {
          path: 'Required'
        };
      }
      if (values.config?.path && !/^\/[0-9a-zA-Z\/\^\?\&\'\@\{\}\[\]\,\$\=\!\-\#\(\)\.\%\+\~_]*$/.test(values.config.path)) {
        errors.config = errors.config ? {
          ...(errors.config as object),
          path: 'Must be a valid pathname and start with "/"'
        } : {
          path: 'Must be a valid pathname and start with "/"'
        };
      }
    }
    if (values.enable_rate_limit && Number(values.config?.rate_limit) <= 0) {
      errors = setIn(errors, 'config.rate_limit', 'Must be greater than 0');
    }
    const delivery_timeout = Number(values.config?.delivery_timeout);
    const delivery_timeout_in_seconds = getDeliveryTimeoutInSeconds(values.config?.delivery_timeout, values.config.delivery_timeout_unit);
    if (!Number.isInteger(delivery_timeout) || delivery_timeout <= 0) {
      errors = setIn(errors, 'config.delivery_timeout', 'Must be a positive whole number');
    } else if (delivery_timeout_in_seconds > MAX_DESTINATION_DELIVERY_TIMEOUT_SECONDS) {
      errors = setIn(errors, 'config.delivery_timeout', values.config.delivery_timeout_unit === 'minute' ? 'Must be 15 minutes or less' : 'Must be 900 seconds or less');
    }
    return cleanseFormErrorObject({
      ...errors,
      ...(await validateDeliveryGroupsConfiguration(values, supports_delivery_groups))
    });
  },
  Fields: ({
    prefix,
    disable_name,
    hide_max_delivery_rate = false,
    destination_radio_cards = false,
    hide_destination_type = false,
    advanced_configuration_display = 'accordion'
  }: {
    prefix: string;
    disable_name?: boolean;
    hide_max_delivery_rate?: boolean;
    destination_radio_cards?: boolean;
    hide_destination_type?: boolean;
    advanced_configuration_display?: 'accordion' | 'section';
  }) => {
    const {
      destination_types
    } = useContext(DashboardContext);
    const {
      configs
    } = useContext(GlobalContext);
    const [{
      value: show_advanced
    },, {
      setValue: setShowAdvanced
    }] = useField<boolean>(fieldName('show_advanced', prefix));
    const [{
      value: type
    }] = useField<DestinationType>(fieldName('type', prefix));
    const selected_type = destination_types?.[type];
    const configuration_alert = selected_type?.configuration_alert;
    const type_features = {
      ...(selected_type?.features || {})
    };
    const max_delivery_rate_feature = hide_max_delivery_rate ? undefined : type_features.MAX_DELIVERY_RATE as DestinationFeatureType | undefined;
    const {
      required_features,
      advanced_features
    } = useFeatures(type_features, 'auth_method');
    const show_advanced_as_section = advanced_configuration_display === 'section';
    const show_delivery_groups = !!max_delivery_rate_feature && supportsDeliveryGroups(max_delivery_rate_feature);
    const show_delivery_timeout = configs.LONG_TIMEOUT_ATTEMPT_ROUTING_ENABLED;
    const show_advanced_configuration = advanced_features.length > 0 || show_delivery_timeout;
    return <>
        {!hide_destination_type && <SelectDestinationType key={type} prefix={prefix} destination_radio_cards={destination_radio_cards} />}
        {selected_type && <>
            {configuration_alert && <Alert inline info m={{
          y: 4
        }}>
                {configuration_alert}
              </Alert>}
            {disable_name !== true && <Div m={{
          y: 4
        }}>
                <resource_details_form_props.Fields prefix={prefix} placeholder={selected_type.label.toLowerCase()} name_prefix="Destination" help="Used to identify your destination while using Hookdeck." name_required />
              </Div>}
            <Div m={{
          y: 4
        }}>
              {required_features.map(feature => <DestinationConfigurationFeature feature={feature as DestinationFeatureType} prefix={prefix} key={feature.type} />)}
            </Div>
            {show_advanced_configuration && <Div m={{
          y: 4,
          t: 4
        }}>
                <Div flex={{
            justify: 'flex-start',
            align: 'center',
            gap: show_advanced_as_section ? 2 : 1
          }}>
                  {show_advanced_as_section ? <Text size="xs" semi muted white_space="nowrap" style={{
              flexShrink: 0
            }}>
                      Advanced configuration
                    </Text> : <ClickableArea p={{
              y: 0,
              x: 1
            }} m={{
              l: -1
            }} rounded style={{
              width: 'auto'
            }} flex={{
              justify: 'flex-start',
              align: 'center',
              gap: 1
            }} onClick={() => setShowAdvanced(!show_advanced)}>
                      <Text size="xs" semi muted={!show_advanced}>
                        Advanced configuration
                      </Text>
                      <Icon muted icon={show_advanced ? 'arrow_drop_down' : 'arrow_right'} />
                    </ClickableArea>}
                  {(show_advanced || show_advanced_as_section) && <Divider />}
                </Div>
                {(show_advanced || show_advanced_as_section) && <>
                    {advanced_features.map(feature => <DestinationConfigurationFeature feature={feature as DestinationFeatureType} prefix={prefix} key={feature.type} />)}
                    {show_delivery_timeout && <DeliveryTimeoutFeature prefix={prefix} />}
                  </>}
              </Div>}
            {max_delivery_rate_feature && <DeliveryPolicyConfiguration feature={max_delivery_rate_feature} prefix={prefix} show_delivery_groups={show_delivery_groups} show_title />}
          </>}
      </>;
  }
};
export default destination_configuration_form_props;